import { isSSR } from './dom';

// The SDK is loaded by a deferred script tag and initialized from a polling
// interval (`render_sentry_script_tag` and `render_error_tracking_script`),
// while the React chunks are injected as async scripts (_inject_tags.html.erb).
// Hydration therefore often runs before Sentry can accept anything, and what is
// captured before `Sentry.init` is dropped without a trace. `window.popmenuSentry`
// is created by the same scripts before the bundles run and resolves in the tick
// that calls `Sentry.init`; it is absent when error tracking is off, in which
// case there is nothing to report to.
const reportToSentry = (report: (sentry: NonNullable<Window['Sentry']>) => void) => {
  void window.popmenuSentry?.then(report);
};

const capturedMessages = new Set<string>();
export const captureSentryMessage = (message: string) => {
  console.error(message);
  if (isSSR || capturedMessages.has(message)) return;

  capturedMessages.add(message);
  reportToSentry(sentry => sentry.captureMessage(message));
};

const capturedExceptions = new Set<string>();

/**
 * Report a caught error to Sentry, at most once per page load for each distinct
 * error, with `extra` attached as additional context.
 *
 * The error is `unknown` to avoid type assertions in `catch` and error callbacks.
 *
 * Unlike {@link captureSentryMessage} this does not log to the console, so that
 * callers can log whatever they find useful in addition to the error itself.
 *
 * @example
 * captureSentryException(error);
 * captureSentryException(error, { cart: menuItemCart });
 */
export const captureSentryException = (error: unknown, extra?: Record<string, unknown>) => {
  if (isSSR) return;

  const key = error instanceof Error ? error.stack ?? `${error.name}: ${error.message}` : String(error);
  if (capturedExceptions.has(key)) return;

  capturedExceptions.add(key);
  reportToSentry(sentry => sentry.captureException(error, extra && { extra }));
};
